import { createContext, Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { ConfirmationFormData } from 'components/tables/data/revision/actions/confirm/GatherConfirmationProvider';
import { Modal, Stack } from '@mantine/core';
import Button from 'components/Button';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import P1Regular from 'components/typography/P1Regular';
import ConfirmationRow from 'components/tables/data/revision/actions/confirm/ConfirmationRow';
import { Box } from '@mantine/core';
import { Group } from '@mantine/core';
import {
  IAutomationGatherConfirmation,
  IPipeline,
} from 'pages/revisions-module/template-editor/editors/pipeline/types';
import { useForm } from '@mantine/form';
import { RevisionGetResponse as Revision } from 'api/actions/revision-get/revision-get-response';
import { useDisclosure } from '@mantine/hooks';
import useLoadingAction from 'hooks/use-loading-action';
import { useApi } from 'api/api-context';

type OnConfirmCallback = (confirmations: Record<string, ConfirmationFormData>) => void | Promise<void>;

interface ConfirmStepParams {
  title?: string;
  revision: Revision;
  onConfirm: OnConfirmCallback;
}

interface ConfirmStepModalProps extends ConfirmStepParams {
  opened: boolean;
  onClose: () => void;
}

type ConfirmStep = (params: ConfirmStepParams) => void;

interface IConfirmStepContext {
  gatherConfirmation: ConfirmStep;
}

const ConfirmStepContext = createContext<IConfirmStepContext>(undefined!);

/**
 * Modal for confirming a step.
 */
function ConfirmStepModal({ revision, opened, onClose, title = 'Potvrdenie', onConfirm }: ConfirmStepModalProps) {
  const [{ loading }, confirm] = useLoadingAction(onConfirm);
  const { userId } = useApi();

  const confirmations: IAutomationGatherConfirmation[] = useMemo(() => {
    const pipeline = JSON.parse(revision?.revisionTemplate?.pipelineScheme || 'null') as IPipeline | null;
    const automations = pipeline?.automations || [];
    const confirmations: IAutomationGatherConfirmation[] = [];

    for (const automation of automations) {
      if (
        automation.type === 'gather-confirmation' &&
        automation.statusSource === 7 &&
        automation.statusTarget === 8 &&
        !(userId !== null && (automation.bypassUserIds ?? []).includes(userId))
      ) {
        confirmations.push(automation);
      }
    }

    return confirmations;
  }, [revision, userId]);

  const initialValues = useMemo(
    () => confirmations.reduce((acc, curr) => ({ ...acc, [curr.id]: undefined }), {}),
    [confirmations]
  );

  const validate = useMemo(
    () => confirmations.reduce((acc, curr) => ({ ...acc, [curr.id]: createValidator([required]) }), {}),
    [confirmations]
  );

  const confirmAndClose = useCallback(
    async (values: Record<string, ConfirmationFormData>) => {
      await confirm(values);
      onClose();
    },
    [confirm, onClose]
  );

  const form = useForm({
    initialValues,
    validate,
  });

  const subHeadline =
    confirmations.length > 0
      ? revision.kind === 'revision'
        ? 'Prebratie revíznej správy vyžaduje nasledujúce potvrdenia:'
        : 'Prebratie formulára vyžaduje nasledujúce potvrdenia:'
      : revision.kind === 'revision'
      ? 'Naozaj si prajete prebrať túto revíznu správu?'
      : 'Naozaj si prajete prebrať tento formulár?';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="auto"
      centered
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit(confirmAndClose)}>
        <Stack spacing={24}>
          <P1Regular>{subHeadline}</P1Regular>
          <Stack spacing={12}>
            {confirmations.map((confirmation, index) => (
              <Fragment key={confirmation.id}>
                <ConfirmationRow
                  label={confirmation.label}
                  autosuggestDataType={confirmation.dataType}
                  index={index}
                  {...form.getInputProps(confirmation.id)}
                />
                <Box w="100%" h={1} bg="gray.0" />
              </Fragment>
            ))}
          </Stack>
          <Group position="right">
            <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" loading={loading} disabled={!form.isValid()}>
              Prebrať
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * Provider for gathering confirmation.
 */
export function ConfirmStepProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<ConfirmStepParams | undefined>(undefined);

  /**
   * Opens the modal for gathering confirmation.
   */
  const gatherConfirmation: ConfirmStep = useCallback((params) => {
    setParams(params);
    open();
  }, []);

  const value = useMemo(() => ({ gatherConfirmation }), [gatherConfirmation]);

  return (
    <ConfirmStepContext.Provider value={value}>
      {children}
      {opened && <ConfirmStepModal opened onClose={close} {...params!} />}
    </ConfirmStepContext.Provider>
  );
}

/**
 * Hook to use the gather confirmation context.
 */
export function useConfirmStep() {
  return useContext(ConfirmStepContext);
}
